/** @jsxImportSource @emotion/react */
import React, {
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import isEqual from 'lodash/isEqual';
import { Button } from '@mui/material';
import { addClass, toggleClass } from '@grapecity/wijmo';
import { DataMap, CellType } from '@grapecity/wijmo.grid';
import { IconButton } from 'components/buttons/IconSVG';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import CustomDialog from 'components/modals/common/CustomDialog';
import CustomGrid from 'components/grids/CustomGrid';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import TableContainer from '@mui/material/TableContainer';
import { CustomInputText, CustomInputWrap, tx } from 'components/inputs/CustomInput';
import { tb } from 'components/layouts/Table';
import TableRow from '@mui/material/TableRow';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TextareaAutosize from 'react-textarea-autosize';
import {
  SubTitleGroup,
  SubTitleLayout,
  HalfContetntLayout,
  ControlBtnGroup,
} from 'components/layouts/ContentLayout';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import { Code } from 'models/common/CommonCode';
import {
  EquipmentMasterCondition,
  Equipment,
  EquipmentLevel,
  CustomEquipment,
} from 'models/common/popup/EquipmentMaster';
import { CrudCode } from 'models/common/Edit';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { findEquipmentMaster } from 'apis/common/popup/EquipmentMaster';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { useMessageBar } from 'hooks/useMessageBar';
import _, { isNull } from 'lodash';
import { EquipmentGroupCondition } from '../../../models/common/popup/EquipmentGroup';
import { findPlant } from '../../../apis/common/Plant';
import UserModal from '../../../components/modals/common/UserModal';
import { Employee } from '../../../models/admin/Employee';
import { useLoading } from 'components/process/Loading';
import { useCommonModal } from 'hooks/useCommonModal';
import { EquipmentGroupPopUp } from 'pages/common/popup/EquipmentGroupPopUp';

/**
 * @description 설비추가 팝업(공용 - 설비변경점, 불용자산)
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 *  25.10.14    jihwan89       설비변경점 개선사항 v5.2 70행 - 개조담당자 일괄지정 기능 추가
 *  25.10.25    jshwang12      설비팝업 유형 추가 (설비셋업), 공정 공통코드 변경(PRDN_PROC_CD), 설비셋업인 경우 공정노출조건 수정
 *  25.10.29    jihwan89       CSR E20251027-000124 설비 추가시 설비레벨 디폴트로 main 추가 (기존: machine) defautCondition 수정
 **/

interface Props {
  open: boolean;
  close: () => void;
  title: string;
  condition?: {
    copCds?: string[]; // 법인코드
    copCd?: string;
    processCode?: string; //공정
    equipmentGroup?: string; //설비군
    prdnPlntCd?: string; //공장(플랜트)
    equipmentId?: string; // 설비ID
    equipmentName?: string; // 설비명
    equipmentLevel?: string; //설비레벨
    equipmentLevels?: string[]; //설비레벨
    mainEqpNm?: string; // 최상위설비명
    chgRsnCtn?: string; //변경사유
  };
  defaultEquipment?: CustomEquipment | CustomEquipment[]; // 기존 선택된 설비정보 목록
  editable?: boolean; // 편집가능 여부 (default : false)
  singleSelect?: boolean; // 단일선택 여부 (default: true)
  isCopCdFix?: boolean; // 법인코드 고정 여부
  isProcCdFix?: boolean; //공정코드 고정 여부
  isEqpGrFix?: boolean; // 설비군 고정 여부
  isPrdnPlntFix?: boolean; //공장(플랜트) 고정 여부
  isMpItem?: boolean; //mp item 여부
  type?: 'SETUP'; // 설비팝업 유형
  isFixedMainLevel?: boolean; // 설비레벨 Main 고정여부 (default: false)
  isCortCrgeUser?: boolean; //개조담당자 사용 여부
  isChgRsnCtn?: boolean; // 변경사유 사용 여부
  isEqpMainLevel?: string; // 기본 설비레벨값
  onCallback: (result: any) => void;
}
//CustomEquipment | CustomEquipment[]

const IdlAssetEquipmentPopUp = ({
  open,
  close,
  title,
  isFixedMainLevel = false,
  // isEqpGrFix = false,
  // isPrdnPlntFix = false,
  // isProcCdFix = false,

  singleSelect = true,
  editable = false,
  onCallback,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const searchRef = useRef<any>();
  const selectRef = useRef<any>();
  const [code, setCode] = useState<any>();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();

  //설비군팝업
  const [isOpenEquipmentGroupPopUp, setOpenEequipmentGroupPopUp] = useState<boolean>(false);

  const [getChgRsnCtn, setChgRsnCtn] = useState<string>('');

  const defaultCondition = Object.assign(
    {
      copCd: '', //법인
      processCode: '', //공정
      equipmentGroup: '', //설비군
      factoryCode: '', //공장(동)
      prdnPlntCd: '', //공장(플랜트)
      equipmentId: '', //설비id
      equipmentDesc: '', //설비명
      mainEquipmentName: '', //
      equipmentLevel: '',
      equipmentLevels: [],
    },
    {
      ...(props?.condition || {}),
      equipmentLevels: props.condition?.equipmentLevels,
    }
  );

  const [condition, setCondition] = useState<EquipmentMasterCondition>(defaultCondition);

  useEffect(() => {
    if (props && props.defaultEquipment) {
      setChgRsnCtn(condition?.chgRsnCtn || '');
    }
    getCommonCodes();
  }, []);

  const getCommonCodes = async () => {
    const elmCopCd: Code[] = await getCommonCodeNames('ELM_COP_CD'); // 법인코드
    const processCode: Code[] = await getCommonCodeNames('PRDN_PROC_CD'); // 공정코드 = factory // PROCESS_CODE
    const factoryCode: Code[] = await getCommonCodeNames('FACTORY_CODE'); // 공장(동)코드
    const equipmentLevel: Code[] = await getCommonCodeNames('EQUIPMENT_LEVEL'); // 설비레벨
    //A : Assembly, C : Machine, M : Main, U : Unit

    const factoryCodef = factoryCode.filter((o) => o.optValCtn1 === condition.copCd);

    const prdnPlntCode = await findPlant(); //공장(플랜트)코드
    const code: Code[] = [];
    prdnPlntCode.forEach((factoryCodeItem) => {
      code.push({ cmnCd: factoryCodeItem.prdnPlntCd, cmnCdNm: factoryCodeItem.prdnPlntNm });
    });

    const prdnProcCd = (processCode || [])
      // [25.10.25] 셋업관리인 경우 투자적용여부(옵션3) Y인 공정만 노출
      .filter((o) => (props.type === 'SETUP' ? o.optValCtn3 === 'Y' : true))
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            ...cur,
            cmnCdNm: cur.cmnCdDesc,
          },
        ],
        [] as Code[]
      );
    setCode({
      elmCopCd: elmCopCd,
      processCode: prdnProcCd,
      prdnPlntCode: code,
      factoryCode: factoryCodef,
      equipmentLevel: equipmentLevel,
    });
  };

  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setCondition({ ...condition, [e.target.name]: e.target.value });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchRef.current?.handleSearch(condition);
    }
  };

  const handleSearch = (condition: EquipmentMasterCondition) => {
    searchRef.current?.handleSearch(condition);
  };

  const handleConfirm = () => {
    if (singleSelect) {
      const items = searchRef.current?.getRowData();
      // [as-is] props.condition?.equipmentLevel === EquipmentLevel.MACHINE &&
      if (
        (defaultCondition?.equipmentLevels || []).includes(EquipmentLevel.MACHINE) &&
        !!props.isCortCrgeUser &&
        (_.isEmpty(items.cortCrgeUserId) || _.isEmpty(items.cortCrgeUserNm))
      ) {
        openMessageBar({
          type: 'error',
          content: t('mp.msg.개조개선 담당자를 입력해 주세요.', '개조개선 담당자를 입력해 주세요.'),
        });
        return;
      }
      onCallback && onCallback((items || []).length ? items[0] : null);
    } else {
      if (!selectRef.current?.validate()) {
        return;
      }

      if (!isNull(props.isChgRsnCtn) && props.isChgRsnCtn && !getChgRsnCtn) {
        openMessageBar({
          type: 'error',
          content: t('com.msg.설비변경사유를 입력하세요.', '설비변경사유를 입력하세요.'),
        });
        return;
      }

      const items = selectRef.current?.getRowData();
      const emptyUser = items
        .map((data) => {
          // [as-is] props.condition?.equipmentLevel === EquipmentLevel.MACHINE &&
          if (
            (defaultCondition?.equipmentLevels || []).includes(EquipmentLevel.MACHINE) &&
            !!props.isCortCrgeUser &&
            (_.isEmpty(data.cortCrgeUserId) || _.isEmpty(data.cortCrgeUserNm))
          ) {
            return `${t(
              'mp.msg.개조개선 담당자를 입력해 주세요.',
              '개조개선 담당자를 입력해 주세요.'
            )}`;
          }
        })
        .filter((element) => element !== undefined);

      if (emptyUser.length) {
        const content = emptyUser[0]?.toString();
        openMessageBar({ type: 'error', content: content || '' });
        return false;
      }

      if ((defaultCondition?.equipmentLevels || []).includes(EquipmentLevel.MACHINE)) {
        if (items.length === 0) {
          openCommonModal({
            modalType: 'confirm',
            content: String(
              t(
                'mp.msg.추가된 설비가 없습니다. 진행하시겠습니까?.',
                '추가된 설비가 없습니다. 진행하시겠습니까?'
              )
            ),
            yesCallback: () => {
              const updatedData = items.map((row) => ({
                ...row,
                chgRsnCtn: getChgRsnCtn,
              }));
              const ef = { eqpList: updatedData, remark: getChgRsnCtn };

              onCallback && onCallback(ef);
              close();
            },
          });
          return false;
        }
      }

      const updatedData = items.map((row) => ({
        ...row,
        chgRsnCtn: getChgRsnCtn,
      }));
      const ef = { eqpList: updatedData, remark: getChgRsnCtn };
      onCallback && onCallback(ef);
    }
    close();
  };

  /**
   * Quick Select 이벤트 처리
   * @param item
   */
  const handleQuickSelect = (item: Equipment) => {
    const result = { eqpList: item, remark: getChgRsnCtn };
    onCallback && onCallback(result);
    close();
  };

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={handleConfirm}
      disableRipple
    >
      {t('com.button.선택', '선택')}
    </Button>,
  ];
  return (
    <CustomDialog
      title={title}
      open={open}
      onClose={close}
      size="xl"
      onCancel={close}
      buttons={dialogButtons}
    >
      {/* 조회조건 시작 */}
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            {(props.isMpItem || 'SETUP' === props.type) && (
              <SearchRows className="FourCol">
                <SearchCols className={'width25p'}>
                  <label>{t('asst.label.법인', '법인')}</label>
                  <ComboBox
                    options={code?.elmCopCd}
                    placeholder={String(t('asst.label.법인을 선택해주세요', '법인을 선택해주세요'))}
                    readOnly={props.isCopCdFix}
                    defaultValue={condition?.copCd} // 변수로 변경해서 사용
                    onChange={(value) => {
                      setCondition((prev) => ({
                        ...prev,
                        copCd: value,
                      }));
                    }}
                  />
                </SearchCols>

                <SearchCols className={'width25p'}>
                  <label>{String(t('mp.label.공정', '공정'))}</label>
                  <ComboBox
                    placeholder={String(t('mp.msg.공정을 선택해주세요.', '공정을 선택해주세요.'))}
                    options={code?.processCode}
                    defaultValue={condition?.processCode}
                    readOnly={props.isProcCdFix}
                    onChange={(value) =>
                      setCondition({ ...condition, processCode: value as string })
                    }
                  />
                </SearchCols>

                <SearchCols className={'width25p'}>
                  <label>{String(t('com.label.설비군', '설비군'))}</label>
                  <CustomInputWithSearch
                    name="equipmentGroup"
                    className={props.isEqpGrFix ? '' : 'find'}
                    placeholder={String(
                      t('mp.msg.설비군을 선택해 주세요.', '설비군을 선택해 주세요.')
                    )}
                    value={condition.equipmentGroup}
                    onChange={handleConditionChange}
                    onSearchClick={() => setOpenEequipmentGroupPopUp(true)}
                    readOnly={props.isEqpGrFix}
                    onKeyDown={handleKeyDown}
                  />
                </SearchCols>

                <SearchCols className={'width25p'}>
                  <label>{t('com.label.Factory', 'Factory')}</label>
                  <ComboBox
                    options={code?.prdnPlntCode}
                    placeholder={String(
                      t('asst.label.Factory 선택해주세요', 'Factory 선택해주세요')
                    )}
                    defaultValue={condition?.prdnPlntCd} // 변수로 변경해서 사용
                    readOnly={props.isPrdnPlntFix}
                    onChange={(value) => {
                      setCondition((prev) => ({
                        ...prev,
                        prdnPlntCd: value,
                      }));
                    }}
                  />
                </SearchCols>
              </SearchRows>
            )}

            <SearchRows className="FourCol">
              {(props.isMpItem || 'SETUP' === props.type) && (
                <SearchCols>
                  <label>{t('asst.label.공장(동)', '공장(동)')}</label>
                  <ComboBox
                    options={code?.factoryCode}
                    placeholder={String(
                      t('asst.label.공장(동)을 선택해주세요', '공장(동)을 선택해주세요')
                    )}
                    defaultValue={condition?.factoryCode} // 변수로 변경해서 사용
                    onChange={(value) => {
                      setCondition((prev) => ({
                        ...prev,
                        factoryCode: value,
                      }));
                    }}
                  />
                </SearchCols>
              )}
              {!(props.isMpItem || 'SETUP' === props.type) && (
                <SearchCols className={'width25p'}>
                  <label>{t('asst.label.법인', '법인')}</label>
                  <ComboBox
                    options={code?.elmCopCd}
                    placeholder={String(t('asst.label.법인을 선택해주세요', '법인을 선택해주세요'))}
                    readOnly={props.isCopCdFix}
                    defaultValue={condition?.copCd} // 변수로 변경해서 사용
                    onChange={(value) => {
                      setCondition((prev) => ({
                        ...prev,
                        copCd: value,
                      }));
                    }}
                  />
                </SearchCols>
              )}

              <SearchCols className="width33">
                <label>{t('asst.label.설비ID', '설비ID')}</label>
                <CustomInputWithSearch
                  name="equipmentId"
                  placeholder={String(
                    t('asst.label.설비ID를 입력해 주세요.', '설비ID를 입력해 주세요.')
                  )}
                  value={condition?.equipmentId}
                  onChange={handleConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('asst.label.설비명', '설비명')}</label>
                <CustomInputWithSearch
                  name="equipmentDesc"
                  placeholder={String(
                    t('asst.label.설비명을 입력해 주세요.', '설비명을 입력해 주세요.')
                  )}
                  value={condition?.equipmentDesc}
                  onChange={handleConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('asst.label.설비레벨', '설비레벨')}</label>
                {/* [25.09.19] MultiComboBox의 readonly 스타일이 적용이 안되어있어 Main레벨로 고정인 경우 기존 방식대로 ComboBox 형태로 노출 후 readonly 처리 유지함 */}
                {isFixedMainLevel ? (
                  <ComboBox
                    options={code?.equipmentLevel}
                    placeholder={String(
                      t('asst.label.설비레벨을 선택해주세요', '설비레벨을 선택해주세요')
                    )}
                    defaultValue={condition?.equipmentLevel}
                    readOnly={isFixedMainLevel}
                    onChange={(value) => {
                      setCondition((prev) => ({
                        ...prev,
                        equipmentLevel: value,
                      }));
                    }}
                  />
                ) : (
                  <MultiComboBox
                    id="prdnProcCd"
                    options={code?.equipmentLevel || []}
                    placeholder={String(t('com.label.전체', '전체'))}
                    defaultValue={condition?.equipmentLevels || []}
                    onChange={(value) => {
                      setCondition((prev) => ({
                        ...prev,
                        equipmentLevel: (value || []).join(),
                        equipmentLevels: value,
                      }));
                    }}
                  />
                )}
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            setReset={() => setCondition(defaultCondition)}
            onSearch={() => handleSearch(condition)}
          />
        </SearchBoxRow>
      </SearchBox>
      {singleSelect ? (
        <SearchGrid
          ref={searchRef}
          singleSelect={singleSelect}
          defaultCondition={defaultCondition}
          onQuickSelect={handleQuickSelect}
        />
      ) : (
        <>
          <HalfContetntLayout style={{ marginTop: '10px' }}>
            <div>
              <SearchGrid
                ref={searchRef}
                singleSelect={singleSelect}
                defaultCondition={condition}
                isCopfix={props.isCopCdFix}
                addRow={(items) => selectRef.current?.addRowData(items)}
              />
            </div>

            <div>
              <SelectedGrid
                ref={selectRef}
                singleSelect={singleSelect}
                editable={editable}
                isCopfix={props.isCopCdFix}
                isCortCrgeUser={props.isCortCrgeUser}
                isMpItem={props.isMpItem}
                type={props.type}
                defaultCondition={condition}
                defaultEquipment={props?.defaultEquipment}
              />
            </div>
          </HalfContetntLayout>
          {!isNull(props?.isChgRsnCtn) && props?.isChgRsnCtn && (
            <div>
              <TableContainer css={tb.table} className="colFix3">
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <span className={'dot'}>{t('asst.label.변경사유', '변경사유')}</span>
                      </TableCell>
                      <TableCell>
                        <CustomInputWrap>
                          <CustomInputText
                            id="chgRsnCtn"
                            name="chgRsnCtn"
                            type="text"
                            value={getChgRsnCtn}
                            maxLength={1000}
                            onChange={(e) => {
                              setChgRsnCtn(e.target.value);
                            }}
                          ></CustomInputText>
                        </CustomInputWrap>
                        {/*<div css={tx.textareaWrap}>*/}
                        {/*  <TextareaAutosize></TextareaAutosize>*/}
                        {/*</div>*/}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}
        </>
      )}

      {isOpenEquipmentGroupPopUp && (
        <EquipmentGroupPopUp
          open={isOpenEquipmentGroupPopUp}
          close={() => setOpenEequipmentGroupPopUp(false)}
          type={'mp'}
          condition={{
            equipmentGroup: condition.equipmentGroup || '',
          }}
          onCallback={(value) =>
            setCondition({
              ...condition,
              equipmentGroup: (value as EquipmentGroupCondition).equipmentGroup,
            })
          }
        />
      )}
    </CustomDialog>
  );
};

interface ContentProps {
  singleSelect: boolean;
  editable?: boolean;
  remark?: boolean;
  isCopfix?: boolean;
  isCortCrgeUser?: boolean;
  isMpItem?: boolean;
  type?: 'SETUP';
  defaultCondition?: EquipmentMasterCondition;
  defaultEquipment?: CustomEquipment | CustomEquipment[];
  onQuickSelect?: (item: Equipment) => void;
  addRow?: (items: Equipment[]) => void;
}

const SearchGrid = forwardRef(
  ({ singleSelect, addRow, onQuickSelect, defaultCondition }: ContentProps, ref) => {
    const gridRef = useRef<any>();
    const prevCondition = useRef<any>();
    const { t } = useTranslation();
    const [rowData, setRowData] = useState<Equipment[]>([]);
    const [checkedItems, setCheckedItems] = useState<Equipment[]>([]);
    const { openLoading } = useLoading();

    useImperativeHandle(ref, () => ({
      handleSearch: (condition) => {
        handleSearch(condition);
      },
      getRowData: () => {
        return checkedItems;
      },
    }));

    useEffect(() => {
      if (!isEqual(prevCondition.current, defaultCondition)) {
        prevCondition.current = defaultCondition;
        // handleSearch(defaultCondition);
      }
    }, []); //defaultCondition

    const handleSearch = (condition) => {
      openLoading(true);
      findEquipmentMaster(condition)
        .then((result) => {
          setRowData(result || []);
        })
        .finally(() => openLoading(false));
    };

    const onInitialized = (grid) => {
      gridRef.current = grid;

      grid.hostElement.addEventListener('click', (e) => {
        if (grid.rows.length == 0) return;

        const ht = grid.hitTest(e);
        if (ht.row < 0 || ht.col < 0) return;

        const binding = grid.columns[ht.col].binding;
        const item = grid.rows[ht.row].dataItem;

        if (ht.panel === grid.cells) {
          // Quick Select
          if ('btnSelect' === binding) {
            onQuickSelect && onQuickSelect(item);
          }
        }
      });

      grid.hostElement.addEventListener('dblclick', (e) => {
        if (grid.rows.length == 0) return;

        const ht = grid.hitTest(e);
        if (ht.row < 0 || ht.col < 0) return;

        const item = grid.rows[ht.row].dataItem;

        if (item) {
          onQuickSelect && onQuickSelect(item);
        }
      });
    };

    const handleAddRow = () => {
      addRow && addRow(checkedItems);
    };

    const layoutDefinition = [
      {
        binding: 'btnSelect',
        header: ' ',
        cssClass: 'WijmoRelay',
        cellTemplate: '<Button />',
        width: 39,
        visible: singleSelect,
      },
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        cellTemplate: (grid) => grid.row._idx + 1,
        visible: singleSelect,
      },
      {
        header: String(t('asst.label.법인', '법인')),
        binding: 'copCd',
        align: 'center',
        width: 100,
      },
      {
        binding: 'equipmentId',
        header: String(t('asst.label.설비ID', '설비ID')),
        align: 'left',
        width: 120,
      },
      {
        binding: 'equipmentDesc',
        header: String(t('asst.label.설비명', '설비명')),
        align: 'left',
        width: '*',
      },
    ];

    return (
      <>
        <SubTitleLayout>
          <SubTitleGroup>
            <h3>{t('asst.label.설비', '설비')}</h3>
            <span className="total">
              {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
              {t('com.label.건', '건')}
            </span>
          </SubTitleGroup>
          {!singleSelect && (
            <ControlBtnGroup>
              <Button
                css={IconButton.button}
                className="addRow"
                onClick={handleAddRow}
                disableRipple
              >
                {t('com.label.추가', '추가')}
              </Button>
            </ControlBtnGroup>
          )}
        </SubTitleLayout>
        <CustomGrid
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          height={450}
          isReadOnly={true}
          isFilter={false}
          autoCheck={true}
          rowSelection={singleSelect ? 'single' : 'multiple'}
          allowPinning={false}
          align="center"
          initialized={onInitialized}
          onChangeCheckedItem={(items) => setCheckedItems(items)}
        />
      </>
    );
  }
);

const SelectedGrid = forwardRef(
  (
    {
      singleSelect,
      editable,
      isCopfix,
      isCortCrgeUser,
      isMpItem,
      type,
      defaultCondition,
      defaultEquipment,
    }: ContentProps,
    ref
  ) => {
    const getDefaultEquipment = () => {
      if (singleSelect) {
        return defaultEquipment ? [defaultEquipment as CustomEquipment] : ([] as CustomEquipment[]);
      }
      return (defaultEquipment || []) as CustomEquipment[];
    };

    const gridRef = useRef<any>();
    const { t } = useTranslation();
    const { openMessageBar } = useMessageBar();
    const [rowData, setRowData] = useState<CustomEquipment[]>(getDefaultEquipment());

    const [getCorrUserId, setCorrUserId] = useState<string>('');
    const [checkedItems, setCheckedItems] = useState<CustomEquipment[]>([]);
    const [code, setCode] = useState<any>({});
    const defaultCopCd = useMemo(
      () => ((defaultCondition?.copCds || []).length > 0 ? defaultCondition?.copCds[0] : ''),
      [defaultCondition?.copCds]
    );

    const [userModalCondition, setUserModalCondition] = useState<any>({});
    const [cortCrgeUser, setCortCrgeUser] = useState<string>('');
    const [selectedCortCrgeUser, setSelectedCortCrgeUser] = useState<any>({
      userId: '',
      empNm: '',
    });
    const [isOpenCortCrgeUserModal, setOpenCortCrgeUserModal] = useState<boolean>(false);

    useImperativeHandle(ref, () => ({
      addRowData: (items: Equipment[]) => {
        addRowData(items);
      },
      getRowData: () => {
        return rowData;
      },
      validate: () => {
        if (!editable) {
          return true;
        }
        const valid = (rowData || [])
          .filter((o) => o.crudKey !== CrudCode.DELETE)
          .map((o, index) => {
            if (_.isEmpty(o.copCd)) {
              return (
                `${index + 1} : ` +
                t('asst.label.법인을 선택해 주세요.', `법인을 선택해 주세요.`) +
                '\n'
              );
            }
            if (_.isEmpty(o.equipmentId)) {
              return (
                `${index + 1} : ` +
                t('asst.label.설비ID를 입력해 주세요.', `설비ID를 입력해 주세요.`) +
                '\n'
              );
            }
            if (_.isEmpty(o.equipmentDesc)) {
              return (
                `${index + 1} : ` +
                t('asst.label.설비명을 입력해 주세요.', `설비명을 입력해 주세요.`) +
                '\n'
              );
            }
          })
          .filter((element) => element !== undefined);

        if (valid.length) {
          openMessageBar({ type: 'error', content: valid.toString() });
          return false;
        }
        return true;
      },
    }));

    useEffect(() => {
      getCommonCodes();
    }, []);

    const getCommonCodes = async () => {
      const elmCopCd: Code[] = await getCommonCodeNames('ELM_COP_CD'); // 법인코드

      // const factoryCode: Code[] = await getCommonCodeNames('FACTORY_CODE'); // 공장(동)
      // const mapFactoryList = new DataMap(code?.factoryCode || [], 'cmnCd', 'cmnCdNm');
      //
      // mapFactoryList.getDisplayValues = (item) => {
      //   return (code?.factoryCode || [])
      //     .filter((o) => o.optValCtn1 === item.copCd)
      //     .filter((o) => !!o.cmnCdNm)
      //     .map((o) => o.cmnCdNm || '');
      // };
      const factoryCode = await getCommonCodeNamesCondition({
        optValCtn1: defaultCopCd,
        cmnGrCd: 'FACTORY_CODE',
      }); // 공정

      const prdnProcCd = await getCommonCodeNamesCondition({
        // [25.10.25] 셋업관리인 경우 투자적용여부(옵션3) Y인 공정만 노출
        [`optValCtn${'SETUP' === type ? '3' : '5'}`]: 'Y',
        cmnGrCd: 'PRDN_PROC_CD',
      }); // 공정

      setCode({
        elmCopCd: elmCopCd,
        factoryCode: factoryCode,
        prdnProcCd: (prdnProcCd || []).reduce(
          (acc, cur) => [...acc, { ...cur, cmnCdNm: cur.cmnCdDesc }],
          [] as Code[]
        ),
      });
    };

    const addRowData = (items: Equipment[]) => {
      setRowData((prev) => {
        const newRows = (items || []).reduce((acc, cur) => {
          acc.push({
            ...cur,
            crudKey: CrudCode.CREATE,
            eqpIdExstYn: 'Y', // 설비ID 조회에서 추가한 경우,
            chgRsnCtn: '',
          });
          return acc;
        }, [] as CustomEquipment[]);
        return [...prev, ...newRows].reduce((acc, cur) => {
          if (
            cur.eqpIdExstYn === 'N' ||
            acc.findIndex(({ equipmentId }) => equipmentId === cur.equipmentId) === -1
          ) {
            acc.push(cur);
          }
          return acc;
        }, [] as CustomEquipment[]);
      });
    };

    const onItemFormatter = useEvent((panel, row, col, cell) => {
      if (CellType.ColumnHeader === panel.cellType) {
        const binding = panel.columns[col].binding;
        // 필수항목 (편진모드인 경우에만 필수항목 표시)
        if (
          ['copCd', 'equipmentId', 'equipmentDesc', 'cortCrgeUserNm'].includes(binding) &&
          editable
        ) {
          addClass(cell, 'dot');
        }
      }

      if (CellType.Cell === panel.cellType) {
        const binding = panel.columns[col].binding;
        const item = panel.rows[row].dataItem;

        const readonlyCopCd = item.eqpIdExstYn === 'Y' || isCopfix;

        if ('copCd'.includes(binding)) {
          cell.ariaReadOnly = readonlyCopCd;
          if ('copCd'.includes(binding)) {
            toggleClass(cell, 'WijmoSelect', !readonlyCopCd);
            if (readonlyCopCd && cell.firstElementChild instanceof HTMLButtonElement) {
              cell.firstChild.remove();
            }
          }
        }
        const readonly = item.eqpIdExstYn === 'Y' || isMpItem;
        if (['equipmentDesc', 'factoryCode', 'processCode'].includes(binding)) {
          cell.ariaReadOnly = readonly;
          if (['factoryCode', 'processCode'].includes(binding)) {
            toggleClass(cell, 'WijmoSelect', !readonly);
            if (readonly && cell.firstElementChild instanceof HTMLButtonElement) {
              cell.firstChild.remove();
            }
          }
        }
        if ('cortCrgeUserNm' === binding && isCortCrgeUser) {
          cell.ariaReadOnly = false;
        }
        if (['equipmentDesc', 'factoryCode', 'processCode'].includes(binding) && isCortCrgeUser) {
          cell.ariaReadOnly = true;
        }
      }
    });

    const onBeginningEdit = (grid, e) => {
      const binding = grid.columns[e.col].binding;
      const item = grid.rows[e.row].dataItem;
      const readonly = item.eqpIdExstYn === 'Y';
      const readonlyCopCd = item.eqpIdExstYn === 'Y' || isCopfix;
      if (['copCd', 'equipmentDesc', 'factoryCode', 'processCode'].includes(binding) && readonly) {
        e.cancel = true;
      }
      if ('copCd'.includes(binding) && readonlyCopCd) {
        e.cancel = true;
      }
    };

    const onCellEditEnding = (grid, e) => {
      const data = grid.rows[e.row].dataItem;
      const binding = grid.columns[e.col].binding;

      // 설비id
      if ('equipmentId' === binding) {
        const newVal = grid.activeEditor?.value;
        // 설비ID 중복 확인
        if (
          grid.rows.filter(
            (o, idx) => idx !== e.row && newVal != null && o.dataItem.equipmentId === newVal
          ).length
        ) {
          openMessageBar({
            type: 'error',
            content: t('asst.label.이미 추가한 설비ID 입니다.', '이미 추가한 설비ID 입니다.'),
          });
          e.cancel = true;
          return;
        }
        if (newVal == null || newVal == '') return;
        // 이미 존재하는 설비ID인 경우 기존 설비 데이터로 덮어쓰기
        findEquipmentMaster({ equipmentIdAsst: newVal, copCd: data.copCd }).then((result) => {
          if ((result || []).length) {
            openMessageBar({
              type: 'warning',
              content: t(
                'asst.label.존재하는 설비ID이므로 설비정보를 변경하였습니다.',
                '존재하는 설비ID이므로 설비정보를 변경하였습니다.'
              ),
            });
            const eqp = result[0];
            data.copCd = eqp.copCd;
            data.equipmentId = eqp.equipmentId;
            data.equipmentDesc = eqp.equipmentDesc;
            data.equipmentName = eqp.equipmentDesc;
            data.factoryCode = eqp.factoryCode;
            data.processCode = eqp.processCode;
            data.eqpIdExstYn = 'Y'; // 설비ID존재여부 변경
            gridRef.current?.refresh();
          }
        });
      }
    };

    const onInitialized = (grid) => {
      gridRef.current = grid;
      grid.itemFormatter = onItemFormatter;

      // grid click event
      grid.hostElement.addEventListener('click', (e) => {
        if (grid.rows.length == 0) return;
        const ht = grid.hitTest(e);
        if (ht.row < 0 || ht.col < 0) return;

        if (ht.panel === grid.cells) {
          const item = grid.rows[ht.row].dataItem;
          const binding = grid.columns[ht.col].binding;

          //mp item 개조담당자 선택
          if ('cortCrgeUserNm' === binding) {
            setUserModalCondition({
              userId: item.cortCrgeUserId ?? '',
              target: {
                binding: binding,
                row: ht.row,
                col: ht.col,
              },
            });
            setOpenCortCrgeUserModal(true);
          }
        }
      });
    };

    const getNextCommonName = (items) => {
      const usedNumbers = items
        .map((item) => {
          const match = item.equipmentId?.match(/^Common (\d+)$/);
          return match ? Number(match[1]) : null;
        })
        .filter((num) => num !== null)
        .sort((a, b) => a - b);

      let nextNumber = 1;

      for (const num of usedNumbers) {
        if (num === nextNumber) {
          nextNumber++;
        } else {
          break;
        }
      }

      return `Common ${nextNumber}`;
    };

    const handleAddRow = () => {
      const newEquipmentId = getNextCommonName(rowData);
      const newRow = {
        crudKey: CrudCode.CREATE,
        copCd: defaultCondition?.copCd,
        eqpIdExstYn: 'N',
        equipmentId: newEquipmentId,
        equipmentDesc: '설비공통',

        // cortCrgeUserId: '',
      } as Equipment;
      const rows = [newRow, ...rowData];
      setRowData(rows);
    };

    const handleDelRow = () => {
      setRowData((prev) => (prev || []).filter((o) => !(checkedItems || []).includes(o)));
      setCheckedItems([]);
    };

    const layoutDefinition = useMemo(() => {
      return [
        {
          binding: 'no',
          header: String(t('com.label.NO', 'NO')),
          width: 40,
          isReadOnly: true,
          cellTemplate: (grid) => grid.row._idx + 1,
        },
        {
          header: String(t('com.label.상태', '상태')),
          binding: 'crudKey',
          width: 50,
          isReadOnly: true,
          align: 'center',
          cellTemplate: GridStatusCellTemplate,
          visible: editable,
        },
        {
          header: String(t('asst.label.법인', '법인')),
          binding: 'copCd',
          align: 'center',
          width: 100,
          dataMap: new DataMap(code?.elmCopCd || [], 'cmnCd', 'cmnCdNm'),
          visible: false,
        },
        {
          binding: 'equipmentId',
          header: String(t('asst.label.설비ID', '설비ID')),
          align: 'left',
          minWidth: 120,
          width: '*',
          isReadOnly: true,
        },
        {
          binding: 'equipmentDesc',
          header: String(t('asst.label.설비명', '설비명')),
          align: 'left',
          width: 150,
        },
        {
          binding: 'cortCrgeUserId',
          header: String(t('com.label.개조담당자id', '개조담당자id')),
          align: 'left',
          visible: false,
        },

        {
          binding: 'cortCrgeUserNm',
          header: String(t('com.label.개조담당자', '개조담당자')),
          align: 'left',
          width: 150,
          cssClass: 'WijmoFind',
          visible: !!isCortCrgeUser,
          isReadOnly: true,
          cellTemplate: (params) => {
            if (params.value == null || params.value == '') {
              return `<span></span><Button />`;
            } else {
              return `<span>${params.value}</span><Button />`;
            }
          },
        },

        {
          binding: 'equipmentGroup',
          header: String(t('com.label.설비군', '설비군')),
          align: 'center',
          width: 100,
          isReadOnly: true,
          visible: !!isMpItem,
        },
        {
          binding: 'prdnPlntNm',
          header: String(t('asst.label.Factory', 'Factory')),
          align: 'center',
          width: 150,
          isReadOnly: true,
          // dataMap: mapFactoryList,
          visible: !!isMpItem,
        },
        {
          binding: 'prdnPlntCd',
          header: String(t('asst.label.Factory', 'Factory')),
          align: 'center',
          width: 150,
          isReadOnly: true,
          visible: false,
        },
        {
          binding: 'factoryCode',
          header: String(t('asst.label.공장(동)', '공장(동)')),
          align: 'left',
          width: 150,
          dataMap: new DataMap(code?.factoryCode || [], 'cmnCd', 'cmnCdNm'),
        },
        {
          binding: 'processCode',
          header: String(t('asst.label.공정', '공정')),
          align: 'left',
          width: 150,
          dataMap: new DataMap(code?.prdnProcCd || [], 'cmnCd', 'cmnCdNm'),
        },
        {
          binding: 'eqpIdExstYn',
          visible: false,
        },
      ];
    }, [code]);

    const handleAllCortCrgeUserChange = () => {
      gridRef.current.rows.map((o) => {
        o.dataItem.cortCrgeUserId = selectedCortCrgeUser.userId;
        o.dataItem.cortCrgeUserNm = selectedCortCrgeUser.empNm;
      });
      gridRef.current.refresh();
    };

    return (
      <>
        <SubTitleLayout>
          <SubTitleGroup>
            <h3></h3>
          </SubTitleGroup>
          <ControlBtnGroup>
            {!!isCortCrgeUser && (
              <>
                <CustomInputWithSearch
                  name="cortCrgeUser"
                  className="find"
                  placeholder={String(
                    t('mp.msg.개조담당자를 선택해 주세요.', '개조담당자를 선택해 주세요.')
                  )}
                  value={cortCrgeUser || ''}
                  onChange={(e) => {
                    setCortCrgeUser(e.target.value);
                  }}
                  onSearchClick={(e) => {
                    setUserModalCondition({
                      empNm: cortCrgeUser ?? '',
                    });
                    setOpenCortCrgeUserModal(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setUserModalCondition({
                        empNm: cortCrgeUser ?? '',
                      });
                      setOpenCortCrgeUserModal(true);
                    }
                  }}
                />
                <Button
                  css={IconButton.button}
                  className="setting"
                  onClick={handleAllCortCrgeUserChange}
                  disableRipple
                >
                  {t('com.button.일괄적용', '일괄적용')}
                </Button>
              </>
            )}
            {editable && (
              <Button
                css={IconButton.button}
                className="addRow"
                onClick={() => handleAddRow()}
                disableRipple
              >
                {t('com.button.행추가', '행추가')}
              </Button>
            )}
            <Button
              css={IconButton.button}
              className="delRow"
              onClick={() => handleDelRow()}
              disableRipple
            >
              {editable ? t('com.button.행삭제', '행삭제') : t('com.button.삭제', '삭제')}
            </Button>
          </ControlBtnGroup>
        </SubTitleLayout>
        <CustomGrid
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          height={450}
          isReadOnly={!editable}
          isFilter={false}
          autoCheck={!editable}
          rowSelection={'multiple'}
          allowPinning={false}
          allowSorting={false}
          align="center"
          initialized={onInitialized}
          beginningEdit={onBeginningEdit}
          cellEditEnding={onCellEditEnding}
          onChangeCheckedItem={(items) => setCheckedItems(items)}
        />

        {/* 담당자 변경 */}

        {isOpenCortCrgeUserModal && (
          <UserModal
            open={isOpenCortCrgeUserModal}
            close={() => setOpenCortCrgeUserModal(false)}
            title={t('com.label.사용자 조회', '사용자 조회')}
            condition={{
              userId: userModalCondition?.userId || '',
              empNm: userModalCondition.empNm || '',
            }}
            isOnlyUsed={true}
            onCallback={(result) => {
              const target = userModalCondition.target;
              if (target && target?.binding) {
                const user = result as Employee;
                if (target?.row > -1) {
                  const item = gridRef.current?.rows[target.row].dataItem;
                  item[target.binding] = user?.empNm;
                  item[target.binding.replace('Nm', 'Id')] = user?.userId;
                  item.crudKey = CrudCode.UPDATE;
                  gridRef.current?.collectionView.refresh();
                }
              } else {
                const user = result as Employee;
                setSelectedCortCrgeUser({
                  userId: user?.userId,
                  empNm: user?.empNm,
                });
              }
            }}
          />
        )}
      </>
    );
  }
);

SearchGrid.displayName = 'SearchGrid';
SelectedGrid.displayName = 'SelectedGrid';

export default IdlAssetEquipmentPopUp;
